import type { NextPage } from 'next'
import { Fragment } from 'react'
import { useTranslation } from 'next-i18next'
import Head from 'next/head'

import { Footer } from '~/components'
import useLandingPageVariant, { LandingVariant } from '~/landing/hooks/useLandingPageVariant'
import ROUTES from '~/shared/constants/routes.const'
import { useNameBeforeTitle } from '~/utils/tags'
import Metatags from '~/components/Metadata/Metatags'
import LogoSchema from '~/components/Metadata/Schema/LogoSchema'
import WebsiteSchema from '~/components/Metadata/Schema/WebsiteSchema'
import { getRunningQueriesThunk as getRunningHomInfoPromises, getHomInfo } from '~/redux/apis/hom-info-api'
import appConfig from '~/config/app'
import usePreviewSite from '~/hooks/usePreviewSite'
import { getStaticPropsWrapper } from '~/utils/pageWrapper/staticPageWrapper'
import { GetProps } from '~/utils/pageWrapper/types'
import { useCampaignReferrer } from '~/hooks/useReferer'
import LandingV4 from '~/landingV4/LandingV4.page'
import LandingPage from '~/landing/Landing.page'
import RoleSelectionPage from '~/classroom/pages/RoleSelection.page'
import ClassroomGuestGuard from '~/classroom/components/ClassroomGuestGuard'

interface HomePageProps {
  forcedVariant?: LandingVariant
}

/**
 * The school edition has no marketing site, so `/` is role selection instead. Two components rather
 * than one branch, because the marketing page runs hooks that mean nothing to a school.
 *
 * Guarded like the two sign-in screens it feeds: choosing a role is meaningless once you are signed
 * in, so a teacher goes to their class list and anyone else to `/dashboard`.
 *
 * `renderWhileDeciding` because this is the entry point, not a secondary screen: the guard's effect
 * cannot run during SSG, so without it the prerendered body for `/` is empty and role selection
 * exists only after hydration. The cost is that a signed-in visitor may glimpse role selection before
 * the redirect lands — the rare case paying for the common one, rather than the reverse.
 *
 * The marketing branch needs nothing here — `usePreviewSite` already bounces a signed-in visitor off
 * it. That hook is the b2c equivalent, and it has no notion of a school teacher, which is why this
 * edition uses the classroom guard rather than sharing one.
 */
const SchoolHomePage = (): React.ReactElement => {
  const { t } = useTranslation('classroom')

  return (
    <Fragment>
      <Metatags
        canonicalPath={ROUTES.HOME}
        metaDescription={t('roleSelection.metaDescription')}
        title={useNameBeforeTitle(t('roleSelection.metaTitle'))}
      />
      <ClassroomGuestGuard renderWhileDeciding>
        <RoleSelectionPage />
      </ClassroomGuestGuard>
    </Fragment>
  )
}

export const MarketingHomePage = ({ forcedVariant }: HomePageProps): React.ReactElement => {
  const { t } = useTranslation('landing')
  const landingVariant = useLandingPageVariant()
  const abTest = forcedVariant || landingVariant

  usePreviewSite()
  useCampaignReferrer({ isCampaignPage: true })

  return (
    <Fragment>
      <Head>
        <link
          as="image"
          href={`${appConfig.imagesCdnUrl}/home/hero/wave-grad-bg-mobile-v2.svg`}
          media="screen and (max-width: 767px)"
          rel="preload"
        />
      </Head>
      <Metatags
        canonicalPath={ROUTES.HOME}
        keywords={t('keywords')}
        metaDescription={t('metaDescription')}
        title={useNameBeforeTitle(t('title'))}
      />
      <LogoSchema />
      <WebsiteSchema />
      {abTest === 'variant' ? <LandingV4 /> : <LandingPage />}
      <Footer />
    </Fragment>
  )
}

const HomePage: NextPage<HomePageProps> = (props: HomePageProps) =>
  appConfig.isSchoolPlatform ? <SchoolHomePage /> : <MarketingHomePage {...props} />

/**
 * Exported so `/landing-control` and `/landing-variant` can stay on the marketing page in the
 * school edition — they need its namespaces, which the gated `getProps` below does not load.
 */
export const getMarketingProps: GetProps = (store) => async () => {
  store.dispatch(getHomInfo.initiate())
  await Promise.all(store.dispatch(getRunningHomInfoPromises()))

  return {
    props: {
      namespaces: [
        'authenticate',
        'home',
        'landing',
        'abTestLanding',
        'tutorOnDemand',
        'registerAccount',
        'subscription',
        'menu',
        'homebase',
        'navbar',
        'imageAlt',
        'dashboard',
        'search',
        'start',
        'register',
        'onboarding',
      ],
    },
  }
}

export const getProps: GetProps = (store) => (context) => {
  if (appConfig.isSchoolPlatform) {
    return Promise.resolve({ props: { namespaces: ['classroom'] } })
  }

  return getMarketingProps(store)(context)
}

export const getStaticProps = getStaticPropsWrapper(getProps)

export default HomePage
